import type { BusinessAreaDto, LinkItemDto, NavItemDto } from '@/lib/sanity/dto';
import { stegaClean } from '@/lib/utils/stega';

export function hasLinkTarget(item?: LinkItemDto | null): boolean {
    if (!item) return false;

    if (item.linkType === 'external') {
        const href = item.href ? stegaClean(item.href).trim() : '';
        return !!href;
    }

    const slug = item.page?.slug ? stegaClean(item.page.slug).trim() : '';
    const anchor = item.anchor ? stegaClean(item.anchor).trim() : '';

    return !!(slug || anchor);
}

export function resolveHref(item: LinkItemDto, queryParams?: Record<string, string>): string {
    if (item.linkType === 'external') {
        const href = item.href ? stegaClean(item.href).trim() : '';
        return href ?? '#';
    }

    const pageSlug = item.page?.slug ? stegaClean(item.page.slug).trim() : '';
    let base = pageSlug ? `/${pageSlug}` : '/';

    if (item.openInModal) {
        base = `/m${base}`;
    }

    if (queryParams && Object.keys(queryParams).length > 0) {
        const searchParams = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value) searchParams.append(key, value);
        });
        const queryString = searchParams.toString();
        if (queryString) {
            const separator = base.includes('?') ? '&' : '?';
            base = `${base}${separator}${queryString}`;
        }
    }

    const anchor = item.anchor ? stegaClean(item.anchor).trim() : '';
    if (anchor) {
        return `${base}#${anchor}`;
    }

    return base;
}

export function getAnchorId(href: string): string | null {
    const i = href.indexOf('#');
    return i === -1 ? null : href.slice(i + 1) || null;
}

export function getAnchorIds(items: LinkItemDto[]): string[] {
    const ids = items.map((item) => getAnchorId(resolveHref(item))).filter((id): id is string => Boolean(id));

    return Array.from(new Set(ids));
}

/** Whether a nav item is active for the current location. Anchor links only
 *  track scroll position on their own base page; on sub-pages they never match. */
export function isNavItemActive(item: LinkItemDto, pathname: string, activeAnchorId: string): boolean {
    const basePath = resolveHref(item).split('#')[0];

    if (item.anchor) {
        return pathname === basePath && item.anchor === activeAnchorId;
    }

    return pathname === basePath;
}

/**
 * Whether the open page lies below the item's page (a case study under
 * „Case Studies“): the item is then the current section, not the current page.
 */
export function isNavItemAncestor(item: LinkItemDto, pathname: string): boolean {
    if (item.anchor) return false;

    const basePath = resolveHref(item).split('#')[0];

    return basePath !== '/' && pathname.startsWith(`${basePath}/`);
}

/**
 * A Geschäftsbereich as a link. The area is a page, not a nav item — this is
 * the one place that adapts it for the link components, so no view has to
 * assemble the shape itself.
 */
export function businessAreaNavItem(area: BusinessAreaDto): NavItemDto {
    return {
        _key: area._id,
        label: area.title,
        linkType: 'internal',
        page: { slug: area.slug, title: area.title },
    };
}
